import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ─── Types ────────────────────────────────────────────────────────────────── */

interface EmptyStateAction {
  label: string;
  onClick: () => void;
}

interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
}

/* ─── EmptyState ───────────────────────────────────────────────────────────── */

const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ icon: Icon, title, description, action, secondaryAction, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col items-center justify-center text-center py-12 px-4 max-w-[400px] mx-auto',
          className
        )}
        {...props}
      >
        <Icon className="h-12 w-12 text-raios-muted mb-4" strokeWidth={1.5} />
        <h3 className="text-base font-medium text-raios-text-secondary mb-1">
          {title}
        </h3>
        {description && (
          <p className="text-sm text-raios-muted mb-4">{description}</p>
        )}
        {(action || secondaryAction) && (
          <div className="flex items-center gap-3 mt-2">
            {action && (
              <button
                type="button"
                onClick={action.onClick}
                className="inline-flex items-center justify-center rounded-md bg-raios-accent text-white text-sm font-medium px-4 py-2 hover:bg-raios-accent-hover transition-colors cursor-pointer"
              >
                {action.label}
              </button>
            )}
            {secondaryAction && (
              <button
                type="button"
                onClick={secondaryAction.onClick}
                className="inline-flex items-center justify-center rounded-md border border-raios-border bg-transparent text-raios-text-secondary text-sm font-medium px-4 py-2 hover:bg-raios-elevated transition-colors cursor-pointer"
              >
                {secondaryAction.label}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }
);

EmptyState.displayName = 'EmptyState';

export { EmptyState };
export type { EmptyStateProps, EmptyStateAction };
